import {
  extractType,
  isArrayType,
  isPromiseType,
  lowerCase,
  PRIMITIVES,
} from '../utils';
import { cloneDeep, groupBy } from 'lodash';
import { isLegacyType } from '../typeParser';
import { getTypeSuffix } from '../../../../utils/typeUtils';
import { isVeloSite } from '../../../../utils';
import { PARAM_ANNOTATION_STRATEGY } from './types';

export const memberIsNotEmpty = (member) => {
  if (member) {
    return !!(
      member.members?.length > 0 ||
      member.enum ||
      member.params?.length > 0
    );
  }
};

export const isPrimitiveType = (type, store) => {
  return (
    (typeof type === 'string' && PRIMITIVES[lowerCase(type)]) ||
    (store && isEnumType(getMemberFromType(type, store))) // we will check for an enum only if store is provided
  );
};

export const getMemberFromType = (type, store) => {
  if (Array.isArray(type)) {
    if (type?.[0].referenceType) {
      return store.getMemberByType(type?.[0]);
    }
    return;
  }
  return store.getMemberByType(type);
};

export const isEnumType = (member) => member?.enum?.length > 0;

export const getTypeDisplayName = ({
  type,
  store,
  typeDisplayName = '',
  forTypeParameters = false,
  JSLayoutType = false,
}) => {
  if (Array.isArray(type)) {
    return type
      .map((possibleType) =>
        getTypeDisplayName({ type: possibleType, store, JSLayoutType }),
      )
      .join(forTypeParameters ? ', ' : ' | ');
  }

  if (isLegacyType(type)) {
    if (typeof type === 'string' && PRIMITIVES[lowerCase(type)]) {
      return type;
    }
    if (isArrayType(type)) {
      return `Array<${
        getTypeSuffix(typeDisplayName) ||
        getTypeDisplayName({ type: type.typeParams[0], store, JSLayoutType })
      }>`;
    }
    if (isPromiseType(type)) {
      return `Promise<${
        getTypeSuffix(typeDisplayName) ||
        getTypeDisplayName({ type: type.typeParams[0], store, JSLayoutType })
      }>`;
    }
    if (isEnumType(getMemberFromType(type, store))) {
      return 'string';
    }
    if (
      typeof type === 'string' &&
      (store.viewer?.viewerConfig?.displayOptions?.showComplexTypesInTable ||
        JSLayoutType)
    ) {
      return getTypeSuffix(typeDisplayName || type);
    }
    return 'object';
  }

  if (type.nativeType) {
    return type.nativeType;
  }

  if (type.referenceType) {
    /*
    If the reference is to an enum message, then show the type as `string`.
     */
    if (isEnumType(getMemberFromType(type.referenceType, store))) {
      return 'string';
    }

    const shouldShowComplexTypesInTable =
      store.viewer?.viewerConfig?.displayOptions?.showComplexTypesInTable;
    return shouldShowComplexTypesInTable
      ? type.displayName || getTypeSuffix(typeDisplayName || type.referenceType)
      : 'object';
  }

  if (type.complexType) {
    const prefix =
      type.complexType.nativeType ||
      type.complexType.displayName ||
      getTypeSuffix(type.complexType.referenceType);

    return `${prefix}<${getTypeDisplayName({
      type: extractType(type),
      store,
      typeDisplayName: '',
      forTypeParameters: true,
      JSLayoutType,
    })}>`;
  }

  if (type.unionType) {
    return getTypeDisplayName({
      type: type.unionType.type,
      store,
      JSLayoutType,
    });
  }
};

const enumToDocsDisplay = (enums) =>
  enums.map((value) => `\`${value}\``).join(', ');

export const generateEnumDoc = (member) => {
  return `Supported values: ${enumToDocsDisplay(member.enum)}.`;
};

export const transformEnumData = (member) => {
  return [
    {
      name: member.name,
      type: 'string',
      doc: generateEnumDoc(member),
    },
  ];
};

export const getMembers = (store) => {
  return (type) => {
    const extractedType = extractType(type);
    return getMemberFromType(extractedType, store);
  };
};

export const getFirstLevelOfRequiredField = (paramPath) =>
  paramPath.split('.')[0];

export const getTableParams = (
  member,
  requiredFields = [],
  shouldAddOptionalAnnotation = false,
) => {
  const tableData = isEnumType(member)
    ? transformEnumData(member)
    : member.members || member.params;

  const requiredFieldsPerParam = groupBy(
    requiredFields,
    getFirstLevelOfRequiredField,
  );

  const addRequiredFieldsIntoParams = (param) => {
    const paramRequiredFields = requiredFieldsPerParam[param.name];
    if (paramRequiredFields) {
      const paramWithRequiredField = cloneDeep(param); // Only if needed, the param object is copied in order to not change it in docsTree (it's not always done because of performance).
      paramWithRequiredField.requiredFields = paramRequiredFields.filter(
        (field) => field !== param.name,
      );

      if (param.required === undefined) {
        paramWithRequiredField.required = paramRequiredFields.includes(
          param.name,
        );
      }
      return paramWithRequiredField;
    }
    return param;
  };

  const params = tableData.map(addRequiredFieldsIntoParams);

  if (shouldAddOptionalAnnotation) {
    /* If is auto velo docs we should override the optional fields that given by the message" */
    return params.map(addOptionalFieldsIntoParams);
  }

  return params;
};

export function getParamsForNextDepth({
  requiredFields = [],
  member,
  shouldAddOptionalAnnotation,
}) {
  const requiredFieldsForNextDepth = requiredFields.map((fieldName) =>
    fieldName.replace(`${getFirstLevelOfRequiredField(fieldName)}.`, ''),
  );
  return getTableParams(
    member,
    requiredFieldsForNextDepth,
    shouldAddOptionalAnnotation,
  );
}

export const isParamInOneOfGroup = (paramName) => (oneOfGroup) =>
  oneOfGroup.members.some((oneOfMemberName) => oneOfMemberName === paramName);

export const isMemberOfSomeOneOfGroup = (paramName, oneOfGroups = []) => {
  return oneOfGroups.some(isParamInOneOfGroup(paramName));
};

function addOptionalFieldsIntoParams(param) {
  const paramWithOptionalField = cloneDeep(param);
  paramWithOptionalField.optional = !paramWithOptionalField.required;
  return paramWithOptionalField;
}

export function getParamAnnotationStrategy(viewer, JSLayoutType) {
  if (isVeloSite(viewer?.category) || JSLayoutType) {
    return PARAM_ANNOTATION_STRATEGY.OPTIONAL;
  }
  return PARAM_ANNOTATION_STRATEGY.REQUIRED;
}

export function removeNullTypeFromRequiredFields(types) {
  if (Array.isArray(types) && types?.length > 0) {
    return types.filter((type) => type.nativeType !== 'null');
  }
  return types;
}
